import React, { useEffect, useState, Fragment, useRef } from 'react';
import { Observer } from 'mobx-react';
import CloseIcon from '@material-ui/icons/Close';
import { Link } from 'react-router-dom';
import {
  Button,
  Typography,
  Checkbox,
  ListItemText,
  Dialog,
  DialogContent,
  List,
  ListSubheader,
  Divider,
  ListItem,
  AppBar,
  Toolbar,
  Slide,
  IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import { toJS } from 'mobx';
import { CustomCreatableSelect } from '../../util/CustomCreatableSelect';
import { MenuList, filterOptions } from '../../util/ReactSelectOverrides';
import { useRootStore } from '../../../stores/BridgeProvider';
import ConfirmDialog from '../../dealMapper/common/ConfirmDialog';
import { safeEval, safeFalsy } from '../../util/UtilFunctions';
import Spinner from '../../util/Spinner';

const useStyles = makeStyles(theme => ({
  appBar: {
    position: 'relative',
    backgroundColor: '#01a1e7',
  },
  title: {
    color: 'white',
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  subheader: {
    margin: '0.5rem 0',
    lineHeight: 1.5,
  },
  subtext: {
    fontSize: '12px',
  },
}));
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const OrgUserInfo = ({ roles, user, manageHandleOpen, children }) => {
  const { orgStore, authStore, dataStore } = useRootStore();
  const [open, setOpen] = React.useState(false);
  const [isLoading, setLoading] = useState(true);
  const [selected, setSelected] = useState(user.OrgRoles);
  const [updating, setUpdating] = useState(false);
  const [change, setChange] = useState(false);
  const classes = useStyles();
  const { UserId, ContactId } = user;
  const { contacts } = dataStore;

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleManageClose = () => {
    setOpen(false);
  };

  const isSelected = name => selected.indexOf(name) > -1;

  const toggleSelected = (event, value) => {
    const newSelected = [value];
    setChange(true);
    isSelected(value)
      ? setSelected(selected.filter(role => role !== value))
      : setSelected(selected.concat(newSelected));
  };

  const updateUserRole = async (userRoles, userId, contactId) => {
    const filteredRoles = userRoles.filter(r => r !== null);
    try {
      await authStore.request.post(
        `${process.env.REACT_APP_API}/api/organization/user`,
        {
          roles: filteredRoles,
          userId,
          contactId: contactId === '' ? null : contactId,
        },
      );
      setChange(false);
      setUpdating(!updating);
      handleManageClose();
      if (userId === orgStore.userId) {
        orgStore.retrieveConfig();
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      const requests = [];
      if (contacts.length === 0) {
        requests.push(dataStore.retrieveContacts());
      }
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);

  useEffect(() => {
    const initVal = JSON.stringify(user.OrgRoles);
    const sel = JSON.stringify(selected);
    if (sel === initVal) {
      setChange(false);
    }
  }, [selected]);

  const deleteUser = async () => {
    try {
      await authStore.request.delete(
        `${process.env.REACT_APP_API}/api/organization/user`,
        {
          data: {
            userId: UserId,
          },
        },
      );
      setUpdating(!updating);
      handleManageClose();
    } catch (e) {
      console.error(e);
    }
  };
  if (isLoading) return <Spinner />;
  return (
    <Observer
      render={() => {
        useEffect(() => {
          const getOrg = async () => {
            await orgStore.retrieveOrgUsers();
          };
          const initialCalls = async () => {
            await Promise.all([getOrg()]);
          };
          initialCalls();
        }, [updating]);
        return (
          <Formik
            initialValues={{
              Contact: dataStore.getContactFromId(ContactId),
              roles: safeFalsy(selected, []),
              userId: UserId,
              ContactId: safeFalsy(ContactId),
            }}
            render={({ values, setFieldValue }) => {
              const handleCreateContact = async inputValue => {
                setChange(true);
                try {
                  const { data: contactData } = await authStore.request.post(
                    `${process.env.REACT_APP_API}/api/contact`,
                    {
                      Name: inputValue,
                      IsAgent: 0,
                    },
                  );
                  const { id: cId } = contactData;
                  await dataStore.retrieveContacts();
                  const contact = dataStore.getContactFromId(cId);
                  setFieldValue('Contact', contact);
                  setFieldValue('ContactId', cId);
                } catch (e) {
                  console.error(e);
                }
              };
              return (
                <Fragment>
                  <div>
                    <Dialog
                      fullScreen
                      open={open}
                      onClose={handleManageClose}
                      TransitionComponent={Transition}
                    >
                      <AppBar className={classes.appBar}>
                        <Toolbar>
                          <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleManageClose}
                            aria-label="close"
                          >
                            <CloseIcon />
                          </IconButton>
                          <Typography variant="h6" className={classes.title}>
                            User Management
                          </Typography>
                          <Button
                            autoFocus
                            color="inherit"
                            onClick={() =>
                              updateUserRole(
                                selected,
                                user.UserId,
                                values.ContactId,
                              )
                            }
                            disabled={!change || selected.length === 0}
                          >
                            save
                          </Button>
                        </Toolbar>
                      </AppBar>
                      <DialogContent>
                        <List>
                          {user.UserName && (
                            <ListItem divider>
                              <ListItemText
                                primary={`Name: ${user.UserName}`}
                              />
                            </ListItem>
                          )}
                          <ListItem divider>
                            <ListItemText primary={`Email: ${user.Email}`} />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary="Link Contact"
                              primaryTypographyProps={{ variant: 'h5' }}
                              secondary={
                                <CustomCreatableSelect
                                  isSearchable
                                  isClearable
                                  isRequired
                                  placeholder="Contact"
                                  components={{ MenuList }}
                                  value={values.Contact}
                                  options={contacts}
                                  getOptionLabel={option => option.Name}
                                  getOptionValue={option => option.ContactId}
                                  isValidNewOption={(
                                    inputValue,
                                    x,
                                    selectOptions,
                                  ) =>
                                    selectOptions.findIndex(
                                      option => option.Name === inputValue,
                                    ) < 0
                                  }
                                  formatCreateLabel={inputValue =>
                                    `Create "${inputValue}"`
                                  }
                                  getNewOptionData={(
                                    inputValue,
                                    optionLabel,
                                  ) => ({
                                    Name: optionLabel,
                                    ContactId: inputValue,
                                    __isNew__: true,
                                  })}
                                  filterOption={filterOptions}
                                  onCreateOption={handleCreateContact}
                                  onChange={val => {
                                    setFieldValue('Contact', val);
                                    setFieldValue(
                                      'ContactId',
                                      safeEval(() => val.ContactId),
                                      null,
                                    );
                                    setChange(true);
                                  }}
                                />
                              }
                              secondaryTypographyProps={{
                                component: 'div',
                                style: { width: '300px' },
                              }}
                            />
                          </ListItem>
                          <List
                            subheader={
                              <ListSubheader className={classes.subheader}>
                                <Typography variant="h5">Roles</Typography>
                                <div className={classes.subtext}>
                                  To view roles and their clearances{' '}
                                  <Link to="/roles">click here</Link>
                                </div>
                              </ListSubheader>
                            }
                          >
                            <Divider />
                            {roles.map(({ OrgRoleId, Name }) => (
                              <ListItem
                                key={Name}
                                value={OrgRoleId}
                                divider
                                dense
                              >
                                <Checkbox
                                  onClick={e => toggleSelected(e, OrgRoleId)}
                                  checked={isSelected(OrgRoleId)}
                                />
                                <ListItemText primary={Name} />
                              </ListItem>
                            ))}
                          </List>
                          <ConfirmDialog
                            content={() => (
                              <div>
                                {user.OrgRoles.includes(5) ? (
                                  <div>
                                    <h3>Cannot Remove Owner</h3>
                                    <p>
                                      You are attempting to remove the owner of
                                      this organization. This user will have to
                                      relinquish ownership to another user
                                      before they can be removed.
                                    </p>

                                    <p>
                                      If you are the owner and would like to
                                      transfer ownership, head to the "Advanced"
                                      tab in your user management.
                                    </p>
                                  </div>
                                ) : (
                                  <div>
                                    <h3>Remove User?</h3>
                                    <p>
                                      You are about to permanantly delete this
                                      user from your organization. Do you wish
                                      to proceed?
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            actions={({ handleClose }) => (
                              <div style={{ width: '100%' }}>
                                {user.OrgRoles.includes(5) ? (
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'flex-end',
                                      padding: '20px',
                                    }}
                                  >
                                    <Button
                                      color="secondary"
                                      variant="contained"
                                      onClick={() => {
                                        handleClose();
                                      }}
                                    >
                                      Go Back
                                    </Button>
                                  </div>
                                ) : (
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-evenly',
                                      flex: 1,
                                      marginBottom: '1rem',
                                    }}
                                  >
                                    <Button
                                      color="primary"
                                      variant="contained"
                                      onClick={() => {
                                        deleteUser();
                                        handleClose();
                                      }}
                                    >
                                      Yes
                                    </Button>
                                    <Button
                                      color="secondary"
                                      variant="contained"
                                      onClick={() => {
                                        handleClose();
                                      }}
                                    >
                                      No
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          >
                            {({ handleOpen }) => {
                              return (
                                <ListItem>
                                  <div
                                    style={{
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <ListItemText
                                      onClick={() => handleOpen()}
                                      primaryTypographyProps={{
                                        color: 'secondary',
                                      }}
                                      primary="Remove User"
                                      secondary="Permanantly remove user from this organization"
                                    />
                                  </div>
                                </ListItem>
                              );
                            }}
                          </ConfirmDialog>
                        </List>
                      </DialogContent>
                    </Dialog>
                  </div>
                  <div
                    onClick={e => {
                      manageHandleOpen && handleClickOpen(e);
                    }}
                  >
                    {children({ handleClickOpen })}
                  </div>
                </Fragment>
              );
            }}
          />
        );
      }}
    />
  );
};

export default OrgUserInfo;
